import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { getIncidentChecklist } from 'api/canvms/incidents';
import type { IncidentChecklistData } from '../../types/canvision.types';

interface IncidentChecklistProps {
  incidentId: number;
}

const RESPONSE_TYPE_LABELS: Record<number, string> = {
  1: 'Comment',
  2: 'Status',
  3: 'Time',
  4: 'Dropdown',
  5: 'Multi-Select',
  6: 'Attachment',
  7: 'Signature',
  8: 'Date/Time',
};

function IncidentChecklist({ incidentId }: IncidentChecklistProps) {
  const [data, setData] = useState<IncidentChecklistData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchChecklist = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await getIncidentChecklist(incidentId);
        if (!cancelled) {
          setData(result.data);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err.response?.data?.message || 'Failed to load checklist');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchChecklist();
    // Poll every 30s for guard updates
    const interval = setInterval(fetchChecklist, 30000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [incidentId]);

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!data || !data.checklist) {
    return (
      <Typography variant="body2" color="text.secondary">
        No checklist assigned.
      </Typography>
    );
  }

  const { checklist, checkpoints, progress } = data;
  const progressPercent = progress.total > 0 ? (progress.completed / progress.total) * 100 : 0;

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {checklist.Name}
      </Typography>

      {/* Progress bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          sx={{ flex: 1, height: 8, borderRadius: 4 }}
          color={progressPercent === 100 ? 'success' : 'primary'}
        />
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 40 }}>
          {progress.completed}/{progress.total}
        </Typography>
      </Box>

      {/* Checkpoint list */}
      <List dense disablePadding>
        {checkpoints.map((cp) => {
          const completed = !!cp.is_completed;
          return (
            <ListItem key={cp.CheckpointID} disablePadding sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                {completed ? (
                  <CheckCircleIcon color="success" fontSize="small" />
                ) : (
                  <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={cp.Name}
                secondary={
                  completed && cp.selected_status_name
                    ? cp.selected_status_name
                    : completed && cp.Comment
                    ? cp.Comment
                    : cp.Description || undefined
                }
                primaryTypographyProps={{
                  variant: 'body2',
                  sx: completed ? { textDecoration: 'line-through', color: 'text.secondary' } : undefined,
                }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
              <Chip
                label={RESPONSE_TYPE_LABELS[cp.ResponseTypeID] || 'Other'}
                size="small"
                variant="outlined"
                sx={{ ml: 1, fontSize: '0.65rem', height: 20 }}
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
}

export default IncidentChecklist;
